// File: client/src/variant/cardEffects/movement.js
// Movement-altering card effects: Knight Fever, Tiny Queen, Pawn Riot.

import { isInCheck, cloneGameState, applyMoveToBoard } from '../../utils/chessLogic';

export const movementOverride = {
  kind: 'movement_override',

  onPlay: ({ card }) => {
    const pattern = (card.effect_json && card.effect_json.pattern) || 'knight';
    return {
      rule: { movementOverride: pattern },
      message: `${card.name}: all your pieces move like a ${pattern} this turn.`,
    };
  },

  /**
   * Replace all legal moves with knight-style leaps.
   */
  transformLegalMoves: ({ gameState, from }) => {
    const board = gameState.board;
    const piece = board[from.row][from.col];
    if (!piece) return [];

    const isWhite = piece === piece.toUpperCase();
    const offsets = [
      [-2, -1], [-2, 1], [-1, -2], [-1, 2],
      [1, -2], [1, 2], [2, -1], [2, 1]
    ];
    const validMoves = [];

    for (const [dr, dc] of offsets) {
      const r = from.row + dr;
      const c = from.col + dc;

      if (r >= 0 && r < 8 && c >= 0 && c < 8) {
        const targetPiece = board[r][c];
        // Empty or opponent piece
        if (targetPiece === '' || (targetPiece === targetPiece.toUpperCase()) !== isWhite) {
          const move = { row: r, col: c };
          const testState = cloneGameState(gameState);
          applyMoveToBoard(testState, from, move);
          if (!isInCheck(testState.board, isWhite)) {
            validMoves.push(move);
          }
        }
      }
    }
    return validMoves;
  },
};

export const movementLimit = {
  kind: 'movement_limit',

  onPlay: ({ card }) => {
    const { piece = 'Q', maxDistance = 3 } = card.effect_json || {};
    return {
      rule: { movementLimit: { piece, maxDistance } },
      message: `${card.name}: your ${piece} may move at most ${maxDistance} squares this turn.`,
    };
  },

  /**
   * Filter legal moves to stay within the maximum distance constraint.
   */
  transformLegalMoves: ({ from, engineMoves, rules }) => {
    const limit = rules?.movementLimit;
    if (!limit) return engineMoves;

    const { maxDistance = 3 } = limit;
    return engineMoves.filter((m) => {
      const dr = Math.abs(m.row - from.row);
      const dc = Math.abs(m.col - from.col);
      return Math.max(dr, dc) <= maxDistance;
    });
  },
};

export const pawnRiot = {
  kind: 'pawn_riot',

  onPlay: ({ card, gameState, playerColor }) => {
    if (!gameState || !gameState.board) {
      return { message: `${card.name}: board state missing.` };
    }

    const board = gameState.board;
    const isWhite = playerColor === 'white';
    const pawnChar = isWhite ? 'P' : 'p';
    const dir = isWhite ? -1 : 1;
    const immediateMoves = [];

    // Scan the board for pawns belonging to the current player
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if (board[r][c] === pawnChar) {
          const targetRow = r + dir;
          // Verify target is on board and empty
          if (targetRow >= 0 && targetRow < 8 && board[targetRow][c] === '') {
            const move = { row: targetRow, col: c };
            const testState = cloneGameState(gameState);
            applyMoveToBoard(testState, { row: r, col: c }, move);
            
            // Cannot put own king in check
            if (!isInCheck(testState.board, isWhite)) {
              immediateMoves.push({
                from: { row: r, col: c },
                to: move,
              });
            }
          }
        }
      }
    }

    return {
      immediateMoves,
      message: immediateMoves.length > 0
        ? `${card.name}: ${immediateMoves.length} pawns advanced!`
        : `${card.name}: no pawns could legally advance.`,
    };
  },
};
